'use client';

import Image from 'next/image';
import { useProfileImage, cleanGoogleImageUrl } from '@hooks/useProfileImageCache';
import { useState, useEffect } from 'react';

interface ProfileImageProps {
  src: string | null | undefined;
  alt: string;
  size?: number;
  className?: string;
}

export function ProfileImage({ 
  src, 
  alt, 
  size = 96, 
  className = ''
}: ProfileImageProps) {
  // Extract initials for fallback early
  const initials = alt
    ? alt
        .split(' ')
        .map(part => part.charAt(0))
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '?';
  
  // Normalize source URL
  const normalizedSrc = src || '';
  
  // Handle loading errors locally
  const [imgError, setImgError] = useState(false);
  const [directSrc, setDirectSrc] = useState<string | null>(null);
  
  // Use our custom hook for image loading and caching
  const { imageSrc, isGoogleImage, isLoading, hasError } = useProfileImage(normalizedSrc, size);
  
  // Try using the original URL directly cleaned without the hook for Google images
  useEffect(() => {
    if (!imgError && normalizedSrc && isGoogleImage) {
      try {
        const cleanedUrl = cleanGoogleImageUrl(normalizedSrc, size);
        setDirectSrc(cleanedUrl);
      } catch (err) {
        console.error('Error cleaning Google image URL:', err);
        setImgError(true);
      }
    }
  }, [normalizedSrc, isGoogleImage, size, imgError]);
  
  // Reset error state when source changes
  useEffect(() => {
    setImgError(false);
  }, [normalizedSrc]);
  
  // Render loading state
  if (isLoading && !directSrc) {
    return (
      <div 
        className={`rounded-full bg-gray-200 animate-pulse flex items-center justify-center ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }
  
  // Render error/fallback state
  if ((hasError && !directSrc) || (!imageSrc && !directSrc) || imgError) {
    return (
      <div 
        className={`rounded-full bg-blue-600 text-white flex items-center justify-center font-bold ${className}`}
        style={{ width: size, height: size }}
      >
        <span style={{ fontSize: size / 2.5 }}>{initials}</span>
      </div>
    );
  }
  
  // For Google images, use basic img element
  if (isGoogleImage) {
    // Use direct source if available, otherwise use imageSrc from hook
    const finalSrc = directSrc || imageSrc;
    if (!finalSrc) {
      // This shouldn't happen given our conditions above, but just in case
      return (
        <div 
          className={`rounded-full bg-blue-600 text-white flex items-center justify-center font-bold ${className}`}
          style={{ width: size, height: size }}
        >
          <span style={{ fontSize: size / 2.5 }}>{initials}</span>
        </div>
      );
    }
    
    return (
      <div
        className={`rounded-full overflow-hidden ${className}`}
        style={{ width: size, height: size }}
      >
        <img
          src={finalSrc}
          alt={alt}
          width={size}
          height={size}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }
  
  // For regular images, use Next.js Image component
  return (
    <Image
      src={imageSrc || ''}
      alt={alt}
      width={size}
      height={size}
      className={`rounded-full object-cover ${className}`}
      style={{ width: size, height: size }}
      onError={() => setImgError(true)}
    />
  );
} 